import { createSlice } from '@reduxjs/toolkit';

/**
 * Online Assessment Slice
 *
 * Manages state for teacher's online assessment management:
 * - List of online assessments
 * - Dashboard statistics
 * - Analytics data
 * - Creation flow state
 */

const initialState = {
  // List of teacher's online assessments
  assessments: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },

  // Dashboard statistics
  dashboardStats: {
    totalAssessments: 0,
    activeAssessments: 0,
    closedAssessments: 0,
    expiredAssessments: 0,
    totalAttempts: 0,
    completedAttempts: 0,
    uniqueParticipants: 0,
    averageScore: 0
  },

  // Currently viewed assessment analytics
  currentAnalytics: null,
  currentParticipants: [],
  participantsPagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },

  // Creation flow state
  creationState: {
    isCreating: false,
    createdAssessment: null,
    accessCode: null,
    shareableLink: null
  },

  // Modal states
  modals: {
    modeSelection: false,
    openConfig: false,
    classConfig: false,
    shareCredentials: false
  },

  // Selected assessment for operations
  selectedAssessment: null,

  // Loading states
  loading: {
    list: false,
    dashboard: false,
    analytics: false,
    participants: false,
    create: false,
    close: false
  },

  // Error state
  error: null,

  // Last fetched timestamps for caching
  lastFetched: {
    list: null,
    dashboard: null
  }
};

const onlineAssessmentSlice = createSlice({
  name: 'onlineAssessment',
  initialState,
  reducers: {
    // ============ Loading States ============
    setListLoading: (state, action) => {
      state.loading.list = action.payload;
    },
    setDashboardLoading: (state, action) => {
      state.loading.dashboard = action.payload;
    },
    setAnalyticsLoading: (state, action) => {
      state.loading.analytics = action.payload;
    },
    setParticipantsLoading: (state, action) => {
      state.loading.participants = action.payload;
    },
    setCreateLoading: (state, action) => {
      state.loading.create = action.payload;
    },
    setCloseLoading: (state, action) => {
      state.loading.close = action.payload;
    },

    // ============ Error Handling ============
    setError: (state, action) => {
      state.error = action.payload;
      // Reset all loading states on error
      Object.keys(state.loading).forEach(key => {
        state.loading[key] = false;
      });
    },
    clearError: (state) => {
      state.error = null;
    },

    // ============ Assessments List ============
    setAssessments: (state, action) => {
      const { assessments, pagination } = action.payload;
      state.assessments = assessments;
      state.pagination = pagination;
      state.loading.list = false;
      state.lastFetched.list = Date.now();
    },

    // ============ Dashboard Stats ============
    setDashboardStats: (state, action) => {
      state.dashboardStats = action.payload;
      state.loading.dashboard = false;
      state.lastFetched.dashboard = Date.now();
    },

    // ============ Analytics ============
    setCurrentAnalytics: (state, action) => {
      state.currentAnalytics = action.payload;
      state.loading.analytics = false;
    },
    clearCurrentAnalytics: (state) => {
      state.currentAnalytics = null;
    },

    // ============ Participants ============
    setParticipants: (state, action) => {
      const { participants, pagination } = action.payload;
      state.currentParticipants = participants;
      state.participantsPagination = pagination;
      state.loading.participants = false;
    },
    clearParticipants: (state) => {
      state.currentParticipants = [];
      state.participantsPagination = initialState.participantsPagination;
    },

    // ============ Creation Flow ============
    startCreation: (state) => {
      state.creationState = {
        isCreating: true,
        createdAssessment: null,
        accessCode: null,
        shareableLink: null
      };
    },
    setCreatedAssessment: (state, action) => {
      const {
        id, assessmentName, accessCode, shareableLink, duration, maxAttempts, status,
        assessmentType, classId, section, assignedStudentsCount
      } = action.payload;
      state.creationState = {
        isCreating: false,
        createdAssessment: {
          id, assessmentName, duration, maxAttempts, status,
          assessmentType: assessmentType || 'OPEN',
          classId: classId ?? null,
          section: section || '',
          assignedStudentsCount: assignedStudentsCount ?? 0
        },
        accessCode,
        shareableLink
      };
      state.loading.create = false;
    },
    resetCreation: (state) => {
      state.creationState = initialState.creationState;
    },

    // ============ Modal States ============
    openModeSelectionModal: (state, action) => {
      state.selectedAssessment = action.payload;
      state.modals.modeSelection = true;
    },
    closeModeSelectionModal: (state) => {
      state.modals.modeSelection = false;
    },
    openOpenConfigModal: (state) => {
      state.modals.modeSelection = false;
      state.modals.openConfig = true;
    },
    closeOpenConfigModal: (state) => {
      state.modals.openConfig = false;
    },
    openClassConfigModal: (state) => {
      state.modals.modeSelection = false;
      state.modals.classConfig = true;
    },
    closeClassConfigModal: (state) => {
      state.modals.classConfig = false;
    },
    openShareCredentialsModal: (state) => {
      state.modals.openConfig = false;
      state.modals.classConfig = false;
      state.modals.shareCredentials = true;
    },
    closeShareCredentialsModal: (state) => {
      state.modals.shareCredentials = false;
      state.selectedAssessment = null;
      state.creationState = initialState.creationState;
    },
    closeAllModals: (state) => {
      state.modals = initialState.modals;
    },

    // ============ Assessment Selection ============
    setSelectedAssessment: (state, action) => {
      state.selectedAssessment = action.payload;
    },
    clearSelectedAssessment: (state) => {
      state.selectedAssessment = null;
    },

    // ============ Assessment Status Updates ============
    updateAssessmentStatus: (state, action) => {
      const { id, status } = action.payload;
      const index = state.assessments.findIndex(a => a._id === id);
      if (index !== -1) {
        state.assessments[index].status = status;
      }
      state.loading.close = false;
    },

    // ============ Reset ============
    resetOnlineAssessmentState: () => initialState
  }
});

// Export actions
export const {
  setListLoading,
  setDashboardLoading,
  setAnalyticsLoading,
  setParticipantsLoading,
  setCreateLoading,
  setCloseLoading,
  setError,
  clearError,
  setAssessments,
  setDashboardStats,
  setCurrentAnalytics,
  clearCurrentAnalytics,
  setParticipants,
  clearParticipants,
  startCreation,
  setCreatedAssessment,
  resetCreation,
  openModeSelectionModal,
  closeModeSelectionModal,
  openOpenConfigModal,
  closeOpenConfigModal,
  openClassConfigModal,
  closeClassConfigModal,
  openShareCredentialsModal,
  closeShareCredentialsModal,
  closeAllModals,
  setSelectedAssessment,
  clearSelectedAssessment,
  updateAssessmentStatus,
  resetOnlineAssessmentState
} = onlineAssessmentSlice.actions;

// ============ Selectors ============
export const selectAssessments = (state) => state.onlineAssessment.assessments;
export const selectPagination = (state) => state.onlineAssessment.pagination;
export const selectDashboardStats = (state) => state.onlineAssessment.dashboardStats;
export const selectCurrentAnalytics = (state) => state.onlineAssessment.currentAnalytics;
export const selectCurrentParticipants = (state) => state.onlineAssessment.currentParticipants;
export const selectParticipants = (state) => state.onlineAssessment.currentParticipants; // Alias
export const selectParticipantsPagination = (state) => state.onlineAssessment.participantsPagination;
export const selectCreationState = (state) => state.onlineAssessment.creationState;
export const selectModals = (state) => state.onlineAssessment.modals;
export const selectSelectedAssessment = (state) => state.onlineAssessment.selectedAssessment;
export const selectLoading = (state) => state.onlineAssessment.loading;
export const selectError = (state) => state.onlineAssessment.error;
export const selectLastFetched = (state) => state.onlineAssessment.lastFetched;

// Cache validity helpers (5 minutes TTL)
export const isListCacheValid = (state) => {
  const lastFetched = state.onlineAssessment.lastFetched.list;
  if (!lastFetched) return false;
  return Date.now() - lastFetched < 5 * 60 * 1000;
};

export const isDashboardCacheValid = (state) => {
  const lastFetched = state.onlineAssessment.lastFetched.dashboard;
  if (!lastFetched) return false;
  return Date.now() - lastFetched < 5 * 60 * 1000;
};

export default onlineAssessmentSlice.reducer;
